import { defineStore } from 'pinia'
import { localStorage, ExpirableStorage } from '../../utils/storage/index'

import {
  getConfigStoreCacheKey,
  getConfigStorePaths,
} from '../../utils/getConfigStorePaths'
import { readInlineConfig } from '../../utils/bootPayload'

import debug from '../../utils/console'
const logger = debug.withTag('store:ConfigStore')

export interface ConfigStore<T> {
  data: Ref<T>
  isLoading: Ref<boolean>
  load: () => Promise<T>
  update: (data: Partial<T>) => void
  reset: () => void
}

export interface ConfigStoreOptions<T> {
  defaults: T
  configStoreKey: string
  schema: (data: unknown) => T
  onLoad?: (data: T) => void
  onUpdate?: (data: T) => void
  onReset?: () => void
  transform?: (data: T) => T
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

// A type predicate rather than an assertion: the payload came from this very
// store's serialised state, so its shape is the store's own T.
const hasValue = <T>(value: unknown): value is T =>
  value !== undefined && value !== null

/**
 * The data the server rendered this store with, when the page is hydrating.
 * Read through type predicates so the store's own type does not depend on the
 * Nuxt app type, which depends on the stores.
 */
function readHydratedConfig<T>(storeId: string): T | undefined {
  const nuxtApp = useNuxtApp()
  if (!nuxtApp.isHydrating) return undefined
  const pinia: unknown = nuxtApp.payload.pinia
  if (!isRecord(pinia)) return undefined
  const store: unknown = pinia[storeId]
  if (!isRecord(store) || !('data' in store)) return undefined
  const data: unknown = store.data
  return hasValue<T>(data) ? data : undefined
}

export function useConfigStore<T>(props: ConfigStoreOptions<T>) {
  const applySafeTransform = (data: T): T => {
    if (!props.transform) return data

    try {
      return props.transform(data)
    } catch (err) {
      logger.error(
        `[${props.configStoreKey}] Failed to apply transform function:`,
        err
      )
      return data
    }
  }

  return defineStore(props.configStoreKey, () => {
    const storageWrapper = new ExpirableStorage(
      localStorage,
      `app:config`,
      60 * 1000 // 1 minute
    )

    const data = ref<T>(props.defaults)
    const isLoading = ref(false)

    // Resolved in load() once the variant flag has been evaluated. Until then the
    // un-suffixed key is used, which is also the key control resolves to.
    let cacheKey = props.configStoreKey

    const fetchJson = async (url: string): Promise<unknown> => {
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}: ${response.statusText}`)
      }
      return await response.json()
    }

    const fetchConfig = async (urls: string[]) => {
      const errors: string[] = []
      for (const url of urls) {
        // The shell may carry this file already, the same value the endpoint
        // returns. A payload that fails validation drops to the network below.
        const inline = readInlineConfig(url)
        if (inline !== undefined) {
          try {
            return props.schema(inline)
          } catch (error) {
            logger.error(
              `[${props.configStoreKey}] Failed to validate inlined config for ${url}:`,
              error
            )
          }
        }
        try {
          // A relative URL only resolves through Nuxt's $fetch on the server.
          // The browser keeps the plain fetch it had.
          const json: unknown = import.meta.server
            ? await $fetch(url)
            : await fetchJson(url)
          const validated = props.schema(json)
          return validated
        } catch (error) {
          logger.error(
            `[${props.configStoreKey}] Failed to load config from ${url}:`,
            error
          )
          errors.push(`Failed to fetch ${url}: ${error}`)
        }
      }
      throw new Error(`Failed to fetch config: ${errors.join(', ')}`)
    }

    const readCache = (): T | null => {
      // The storage wrapper falls back to one in memory emulator per module,
      // which on a server lives as long as the container, so one request's
      // config would answer the next request's read. The server has its own
      // fetch per request and nothing to gain from a cache, so it skips it.
      if (import.meta.server) return null
      const stored = storageWrapper.load(cacheKey)
      if (!stored) {
        return null
      }
      try {
        return props.schema(JSON.parse(stored))
      } catch (err) {
        logger.error(
          `[${props.configStoreKey}] Failed to parse or validate stored data:`,
          err
        )
        // Drop the bad entry and fall through to the network rather than pinning
        // the store to its defaults for the rest of the cache window.
        storageWrapper.remove(cacheKey)
        return null
      }
    }

    let cacheable = false
    let cacheKeyKnown = false
    let cacheKeyResolved: Promise<string[]> | null = null

    /**
     * Points `cacheKey` at the variant this session actually resolved, and
     * hands back the URLs to load from.
     *
     * Every read and write has to wait for this. A cache keyed only by
     * configStoreKey serves control to a session that resolved a variant, and
     * keeps serving a variant after the override went away. Failing to resolve
     * degrades to the defaults rather than rejecting `initialized`.
     */
    const resolveCacheKey = (): Promise<string[]> => {
      cacheKeyResolved ??= (async () => {
        let variant = ''
        let urls: string[] = []
        try {
          const resolved = await getConfigStorePaths(props.configStoreKey)
          variant = resolved.variant
          cacheable = resolved.cacheable
          urls = resolved.urls
        } catch (err) {
          logger.error(
            `[${props.configStoreKey}] Failed to resolve config paths:`,
            err
          )
        }
        cacheKey = getConfigStoreCacheKey(props.configStoreKey, variant)
        cacheKeyKnown = true
        return urls
      })()
      return cacheKeyResolved
    }

    /**
     * Writes the current value under the session's own key.
     *
     * It waits when the key is not resolved yet, which is the hydrated store:
     * that path never calls `load()`, so `cacheKey` is still the unsuffixed one
     * and that belongs to control. Writing then would put a variant session's
     * config where the next control session reads it.
     */
    const persist = () => {
      // Browser only. The server has its own fetch per request and the storage
      // wrapper it would write into is a module level emulator shared by every
      // request the container handles.
      if (import.meta.server) return
      if (cacheKeyKnown) {
        storageWrapper.save(cacheKey, JSON.stringify(data.value))
        return
      }
      void resolveCacheKey().then(() => {
        storageWrapper.save(cacheKey, JSON.stringify(data.value))
      })
    }

    let loadPromise: Promise<T> | null = null
    const load = async (): Promise<T> => {
      if (loadPromise) {
        return loadPromise
      }

      isLoading.value = true

      const urls = await resolveCacheKey()

      const cached = readCache()

      if (cached) {
        const transformed = applySafeTransform(cached)
        data.value = transformed
        props.onLoad?.(transformed)
      } else {
        try {
          const validated = await fetchConfig(urls)
          const transformed = applySafeTransform(validated)
          data.value = transformed
          if (cacheable && import.meta.client) {
            storageWrapper.save(cacheKey, JSON.stringify(transformed))
          }
          props.onLoad?.(transformed)
        } catch (err) {
          logger.error(
            `[${props.configStoreKey}] Failed to fetch or validate config:`,
            err
          )
        }
        if (!data.value) {
          logger.warn(
            `[${props.configStoreKey}] No valid data found in local storage or API`
          )
        }
      }
      isLoading.value = false
      return await Promise.resolve(data.value)
    }
    // When the server already resolved and fetched this config for the request,
    // the hydrated render keeps that data rather than racing a second load
    // (cache or network) against it.
    const hydrated: T | undefined = import.meta.client
      ? readHydratedConfig<T>(props.configStoreKey)
      : undefined
    if (hydrated) {
      data.value = hydrated
      loadPromise = Promise.resolve(hydrated)
      // No load() on this path, so nothing else would ever resolve the variant
      // key, and update() and reset() persist through it.
      void resolveCacheKey()
    } else {
      // Load the config when the store is created
      loadPromise = load()
    }

    const update = (newData: Partial<T>) => {
      logger.log('updating config store', newData)
      const merged = { ...data.value, ...newData }
      data.value = applySafeTransform(merged)
      persist()
      props.onUpdate?.(data.value)
    }

    const reset = () => {
      const clone = structuredClone(props.defaults)
      data.value = applySafeTransform(clone)
      persist()
      props.onReset?.()
      loadPromise = null
    }

    return {
      data,
      isLoading,
      load,
      update,
      reset,
      storage: storageWrapper,
      initialized: loadPromise,
    }
  })
}
